import {
  createQuery,
  createSuspenseQuery
} from "../../utils/queryFactories.js";
import { api } from "../../utils/fetch.js";
import { z } from "zod";

const TaskAddressSchema = z
  .object({
    formatted_address: z.string().nullish(),
    lat: z.number().nullish(),
    lon: z.number().nullish()
  })
  .passthrough();

export const TaskDetailResponseSchema = z.object({
  id: z.number(),
  archived: z.boolean(),
  address: TaskAddressSchema.nullish(),
  location: z.number().nullable().optional(),
  location_id: z.number().nullable().optional(),
  board: z.number(),
  category: z.number().nullish(),
  category_value: z.string().nullable(),
  client: z.number().nullable(),
  color: z.string().nullable(),
  code: z.string().nullish(),
  description: z.string().nullable(),
  display_name: z.string().nullable(),
  duration: z.number().nullable(),
  order: z.number(),
  status_value: z.string().nullable(),
  style: z.string().nullable(),
  tag: z.array(z.number().nullable()),
  tag_values: z.array(z.string().nullable()),
  task_type: z.number(),
  team_name: z.string().nullable(),
  team_color: z.string().nullable(),
  include_photo: z.boolean(),
  include_document: z.boolean(),
  checklists: z.array(z.number()),
  assigned_resources: z
    .array(
      z.object({
        resource_id: z.number(),
        label: z.string().nullable(),
        start_date: z.string().nullable(),
        end_date: z.string().nullable(),
        is_assigned_to_me: z.boolean().nullish()
      })
    )
    .nullish(),
  board_name: z.string().nullable(),
  phase_value: z.string().nullish(),
  phase: z.number().nullish(),
  checklist_count: z.number().nullish(),
  notes_count: z.number().nullish(),
  files_count: z.number().nullish(),
  budget_id: z.number().nullable(),
  budget_number: z.string().nullable(),
  can_create_file: z.boolean().optional()
});

export type TaskDetailResponse = z.infer<typeof TaskDetailResponseSchema>;

const wfRequest = z.object({
  api: z.literal("wf"),
  id: z.number()
});

const publicRequest = z.object({
  api: z.literal("public"),
  id: z.number(),
  slug: z.string()
});

const TaskDetailRequestSchema = z.discriminatedUnion("api", [
  wfRequest,
  publicRequest
]);

const TaskDetailRequestVariables = z.union([
  wfRequest.omit({ api: true }),
  publicRequest.omit({ api: true })
]);

export type TaskDetailRequest = z.infer<typeof TaskDetailRequestSchema>;

const urls = {
  wf: "/task/:id",
  public: "/public/:slug/task/:id"
};

export const useTaskDetail = createQuery<
  TaskDetailResponse,
  TaskDetailRequest,
  Error
>({
  queryKey: ["taskDetail"],
  staleTime: 1000 * 60,
  fetcher: async (variables) => {
    const { api: taskDetailAPI, ...rest } = variables;
    return api<Omit<TaskDetailRequest, "api">, TaskDetailResponse>({
      method: "GET",
      url: urls[taskDetailAPI],
      requestSchema: TaskDetailRequestVariables,
      responseSchema: TaskDetailResponseSchema,
      body: rest
    });
  }
});

export const useTaskDetailSuspense = createSuspenseQuery<
  TaskDetailResponse,
  TaskDetailRequest,
  Error
>({
  queryKey: ["taskDetail"],
  staleTime: 1000 * 60,
  fetcher: async (variables) => {
    const { api: taskDetailAPI, ...rest } = variables;
    return api<Omit<TaskDetailRequest, "api">, TaskDetailResponse>({
      method: "GET",
      url: urls[taskDetailAPI],
      requestSchema: TaskDetailRequestVariables,
      responseSchema: TaskDetailResponseSchema,
      body: rest
    });
  }
});
